// EstadisticasCatalog.jsx - Catálogo de estadísticas/resultados con estilo de reportes
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import AlertDialog from '../../Shared/AlertDialog';

const nombreTabla = "Estadísticas y Resultados";

export default function EstadisticasCatalog() {
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(0); // 0 = TODOS por defecto
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState([]);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({ open: true, message, variant, title });
    };

    const currentCourse = useMemo(
        () => courses.find((c) => c.idCurso === selectedCourseId) || null,
        [courses, selectedCourseId]
    );

    // Filtrado local por nombre
    const filteredRows = useMemo(() => {
        if (!searchTerm.trim()) return rows;
        const term = searchTerm.toLowerCase();
        return rows.filter(r =>
            (r.nombreCompleto || '').toLowerCase().includes(term) ||
            (r.nombreCurso || '').toLowerCase().includes(term)
        );
    }, [rows, searchTerm]);

    useEffect(() => {
        const loadCourses = async () => {
            setLoading(true);
            try {
                const res = await axios.get(ENDPOINTS.cursos);
                const list = Array.isArray(res.data) ? res.data : [];
                const normalized = list.map((c) => ({
                    idCurso: c.idCurso ?? c.IdCurso,
                    nombre: c.nombre ?? c.Nombre,
                    activo: c.activo ?? c.Activo,
                    terminado: c.terminado ?? c.Terminado,
                })).filter((c) =>
                    Number.isFinite(Number(c.idCurso)) &&
                    (c.activo === true || String(c.activo).toLowerCase() === 'true') &&
                    (c.terminado === true || String(c.terminado).toLowerCase() === 'true')
                );

                setCourses(normalized);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };

        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadStats = async () => {
            // Permitir 0 (TODOS) pero no null/undefined
            if (selectedCourseId == null) {
                setRows([]);
                return;
            }

            setLoading(true);
            try {
                const res = await axios.get(ENDPOINTS.estadisticasCursoByIdCurso(selectedCourseId));
                const data = Array.isArray(res.data) ? res.data : [];
                const normalized = data.map((r) => ({
                    idEstudiante: r.idEstudiante ?? r.IdEstudiante,
                    nombreCompleto: r.nombreCompleto ?? r.NombreCompleto,
                    nombreCurso: r.nombreCurso ?? r.NombreCurso ?? '',
                    promedioGeneral: r.promedioGeneral ?? r.PromedioGeneral,
                    aprobado: r.aprobado ?? r.Aprobado ?? false,
                    terminado: r.terminado ?? r.Terminado ?? false,
                    tieneCertificado: r.tieneCertificado ?? r.TieneCertificado ?? false,
                }));
                setRows(normalized);
            } catch (e) {
                console.error(e);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        loadStats();
    }, [selectedCourseId]);

    // Exportar a Excel (CSV)
    const downloadExcel = () => {
        if (!filteredRows || filteredRows.length === 0) {
            triggerAlert("No hay datos para exportar.", "warning", "Atención");
            return;
        }

        const headers = [
            "Alumno",
            "Curso",
            "Promedio",
            "Terminado",
            "Aprobado",
            "Certificado",
        ];

        const csvRows = [
            headers.join(","),
            ...filteredRows.map((r) => {
                const avg = (r.promedioGeneral == null || Number.isNaN(Number(r.promedioGeneral)))
                    ? ""
                    : `${Math.round(Number(r.promedioGeneral))}%`;

                return [
                    `"${r.nombreCompleto || ''}"`,
                    `"${r.nombreCurso || ''}"`,
                    `"${avg}"`,
                    `"${r.terminado ? 'SÍ' : 'NO'}"`,
                    `"${r.aprobado ? 'SÍ' : 'NO'}"`,
                    `"${r.tieneCertificado ? 'SÍ' : 'NO'}"`,
                ].join(",");
            }),
        ];

        const csvString = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Estadisticas_${currentCourse?.nombre || 'General'}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar" style={{ flexWrap: 'wrap', gap: '1rem' }}>
                    <h2 className="roles-title" style={{ margin: 0 }}>{nombreTabla}</h2>

                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                        <div className="roles-search-box" style={{ width: '300px' }}>
                            <input
                                type="text"
                                className="roles-input"
                                placeholder="Buscar por alumno o curso..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.55rem 1rem',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.9rem'
                                }}
                            />
                        </div>

                        <select
                            className="roles-select"
                            value={selectedCourseId || ''}
                            onChange={(e) => setSelectedCourseId(Number(e.target.value))}
                            style={{
                                minWidth: '280px',
                                padding: '0.55rem 0.9rem',
                                borderRadius: '10px',
                                border: '1px solid #cbd5e1',
                                background: '#ffffff',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                                fontWeight: 600,
                                color: '#0f172a',
                            }}
                        >
                            <option value={0}>TODOS LOS CURSOS</option>
                            {courses.map((c) => (
                                <option key={c.idCurso} value={c.idCurso}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>

                        <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel">
                            📥 Reporte
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="catalog-loading">Cargando estadísticas...</div>
                ) : (
                    <div className="roles-table-wrap">
                        <div className="roles-table-scroller">
                            <table className="roles-table">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '220px' }}>Alumno</th>
                                        <th style={{ minWidth: '180px' }}>Curso</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Promedio</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Terminado</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Aprobado</th>
                                        <th style={{ minWidth: '110px', textAlign: 'center' }}>Certificado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                                {searchTerm ? 'No se encontraron resultados para tu búsqueda.' : 'No hay datos disponibles.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRows.map((r) => {
                                            const avg = (r.promedioGeneral == null || Number.isNaN(Number(r.promedioGeneral)))
                                                ? null
                                                : Math.round(Number(r.promedioGeneral));

                                            const avgBg = avg == null
                                                ? 'transparent'
                                                : avg < 60
                                                    ? '#fee2e2'
                                                    : avg < 80
                                                        ? '#fef3c7'
                                                        : '#dcfce7';

                                            const avgColor = avg == null
                                                ? '#0f172a'
                                                : avg < 60
                                                    ? '#991b1b'
                                                    : avg < 80
                                                        ? '#854d0e'
                                                        : '#166534';

                                            const renderStatus = (val) => (
                                                <span style={{
                                                    padding: '2px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700',
                                                    backgroundColor: val ? '#dcfce7' : '#f1f5f9',
                                                    color: val ? '#166534' : '#64748b',
                                                    border: val ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                                                }}>
                                                    {val ? 'SÍ' : 'NO'}
                                                </span>
                                            );

                                            return (
                                                <tr key={`${r.idEstudiante}-${r.nombreCurso}`}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                background: '#3b82f6',
                                                                color: '#fff',
                                                                display: 'grid',
                                                                placeItems: 'center',
                                                                fontSize: '0.85rem',
                                                                fontWeight: '600'
                                                            }}>
                                                                {(r.nombreCompleto || 'E').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: '600', color: '#1e293b' }}>{r.nombreCompleto || '—'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' }}>
                                                            {r.nombreCurso}
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontWeight: '700' }}>
                                                        {avg == null ? "—" : (
                                                            <span style={{
                                                                padding: '0.25rem 0.6rem',
                                                                borderRadius: '6px',
                                                                background: avgBg,
                                                                color: avgColor,
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                {avg}%
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {renderStatus(r.terminado)}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {renderStatus(r.aprobado)}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {renderStatus(r.tieneCertificado)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
